import { useEffect, useRef, useState } from "react";
import { api } from "../api";

/**
 * Autocomplete de contas CRM. Le em tempo real do backend.
 * Props:
 *  - value: nome atual (string)
 *  - onSelect: ({id, nome, nif}) => void  (chamado quando escolhe da lista)
 *  - onChangeText?: (nome) => void        (chamado ao escrever livre)
 *  - placeholder, testid, disabled, className
 */
export default function AccountPicker({ value = "", onSelect, onChangeText, placeholder = "Escreva o nome do cliente...", testid = "account-picker", disabled = false, className = "" }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [hi, setHi] = useState(-1);
  const wrapRef = useRef(null);
  const debounce = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    if (!open) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      api.get(`/crm/accounts/lookup?q=${encodeURIComponent(q)}&limit=10`)
        .then(({ data }) => setRows(data || []))
        .catch(() => setRows([]));
    }, 180);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [q, open]);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const choose = (r) => {
    setQ(r.nome);
    onSelect && onSelect(r);
    setOpen(false);
  };
  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(rows.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && hi >= 0 && rows[hi]) { e.preventDefault(); choose(rows[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(-1); onChangeText && onChangeText(e.target.value); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testid}
        className="w-full px-3 py-2 border border-tint rounded-lg text-sm"
        autoComplete="off"
      />
      {open && rows.length > 0 && (
        <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-tint rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid={`${testid}-list`}>
          {rows.map((r, i) => (
            <button
              key={r.id}
              type="button"
              onMouseEnter={() => setHi(i)}
              onClick={() => choose(r)}
              data-testid={`${testid}-item-${r.id}`}
              className={`w-full text-left px-3 py-2 text-sm border-b border-tint last:border-b-0 hover:bg-blue/10 hover:text-blue transition-colors ${hi === i ? "bg-blue/10 text-blue" : "text-navy"}`}
            >
              <div className="font-semibold">{r.nome}</div>
              <div className="text-[11px] text-muted2">{r.nif ? `NIF ${r.nif}` : "sem NIF"}{r.tipo ? ` - ${r.tipo}` : ""}</div>
            </button>
          ))}
        </div>
      )}
      {open && rows.length === 0 && q.trim() && (
        <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-tint rounded-lg shadow-lg p-3 text-xs text-muted2" data-testid={`${testid}-empty`}>
          Sem resultados para "{q}". Podera continuar a escrever para criar novo.
        </div>
      )}
    </div>
  );
}
